// ./src/components/features/Vouchers/VouchersList/VouchersList.tsx
//
// Vouchers (coupon type) list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Mirrors the Policies list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { CouponType, CouponTypeItem } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalCouponTypesContext } from "@/contexts/CouponTypesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Props
// ============================================

export interface VouchersListProps {
  data?: CouponType[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditVoucher?: (voucher: CouponType) => void;
  onDeleteVoucher?: (voucher: CouponType) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddVoucher?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no
   * match" — NOT "nothing here yet", so don't offer the first-run Add prompt.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (voucher: CouponType): boolean => voucher.status === STATUSES.DISABLED;

const activeItems = (voucher: CouponType): CouponTypeItem[] =>
  (voucher.items ?? []).filter((i) => (i.name ?? "").trim().length > 0);

const StatusBadge: React.FC<{ voucher: CouponType }> = ({ voucher }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(voucher)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

// The line-item menu — first few items as chips, then a "+N" overflow. Falls
// back to the flat coupon value ("Worth N") when the voucher has no items.
const ItemsSummary: React.FC<{ voucher: CouponType; max?: number }> = ({ voucher, max = 3 }) => {
  const { t } = useTranslation(["vouchers"]);
  const items = activeItems(voucher);

  if (items.length === 0) {
    if (voucher.couponValue != null) {
      return (
        <Badge size="xs" variant="light" color="teal">
          {t("vouchers:list.worth", { defaultValue: "Worth {{value}}", value: voucher.couponValue })}
        </Badge>
      );
    }

    return (
      <Text size="xs" c="dimmed">
        {t("vouchers:list.noItems", { defaultValue: "No items" })}
      </Text>
    );
  }

  const shown = items.slice(0, max);
  const rest = items.length - shown.length;

  return (
    <Group gap={6} wrap="wrap">
      {shown.map((item, i) => (
        <Badge key={item.id ?? i} size="xs" variant="light" color="grape">
          {(item.name ?? "").trim()}
          {(item.quantity ?? 1) > 1 ? ` ×${item.quantity}` : ""}
        </Badge>
      ))}
      {rest > 0 && (
        <Badge size="xs" variant="outline" color="gray">
          {t("vouchers:list.moreItems", { defaultValue: "+{{count}}", count: rest })}
        </Badge>
      )}
    </Group>
  );
};

const UsesBadge: React.FC<{ voucher: CouponType }> = ({ voucher }) => {
  const { t } = useTranslation(["vouchers"]);
  const max = voucher.maxRedemptions ?? 1;

  return (
    <Badge size="xs" variant="light" color="blue">
      {max <= 1
        ? t("vouchers:list.singleUse", { defaultValue: "Single-use" })
        : t("vouchers:list.multiUse", { defaultValue: "{{count}} uses", count: max })}
    </Badge>
  );
};

const VoucherIcon: React.FC<{ voucher: CouponType; size?: number }> = ({ voucher, size = 34 }) => (
  <ThemeIcon variant="light" color={voucher.color || "grape"} radius="xl" size={size}>
    <AppIcon iconName={voucher.icon || "RosetteDiscount"} size={Math.round(size * 0.55)} />
  </ThemeIcon>
);

const RowActions: React.FC<{
  voucher: CouponType;
  onEdit?: (v: CouponType) => void;
  onDelete?: (v: CouponType) => void;
  isDeleting: boolean;
}> = ({ voucher, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(voucher, "edit");
  const showDelete = !!onDelete && recordHasRight(voucher, "remove");

  if (!showEdit && !showDelete) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(voucher)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(voucher)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  voucher: CouponType;
  name: string;
  disabled: boolean;
  onEdit?: (v: CouponType) => void;
  onDelete?: (v: CouponType) => void;
  highlighted: boolean;
  isDeleting: boolean;
  language: string;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const VoucherTableRow: React.FC<RowProps> = ({
  voucher,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => voucher.id && onToggleSelect?.(voucher.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <VoucherIcon voucher={voucher} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge voucher={voucher} />
            </Group>
            {voucher.description && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {voucher.description}
              </Text>
            )}
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 320 }}>
        <ItemsSummary voucher={voucher} />
      </Table.Td>
      <Table.Td style={{ width: 120 }}>
        <UsesBadge voucher={voucher} />
      </Table.Td>
      <Table.Td style={{ width: 160 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(voucher.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions voucher={voucher} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const VoucherTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 320 }}>
      <Skeleton height={18} width={160} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Skeleton height={18} width={80} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={110} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const VoucherCardComponent: React.FC<CardProps> = ({
  voucher,
  name,
  disabled,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const toggleSelect = () => voucher.id && onToggleSelect?.(voucher.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (onEdit && recordHasRight(voucher, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(voucher),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(voucher, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(voucher),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, voucher, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <VoucherIcon voucher={voucher} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge voucher={voucher} />
          </Group>
          <Group gap={6} wrap="wrap" mt={6}>
            <UsesBadge voucher={voucher} />
          </Group>
          <Box mt={6}>
            <ItemsSummary voucher={voucher} max={4} />
          </Box>
        </Box>
        {showActions && (
          <RowActions voucher={voucher} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
        )}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const VoucherCard = React.memo(VoucherCardComponent);

const VoucherCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={['md', 'xs', 'xs']} gap={5} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const VouchersListComponent: React.FC<VouchersListProps> = ({
  data: dataProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditVoucher,
  onDeleteVoucher,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddVoucher,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["vouchers", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEditVoucher, onDeleteVoucher);

  const couponTypesContext = useOptionalCouponTypesContext();

  const vouchers = dataProp ?? couponTypesContext?.couponTypes ?? [];
  const loading = loadingProp ?? couponTypesContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? couponTypesContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? couponTypesContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? couponTypesContext?.loadMore;
  const isRemoving = isRemovingProp ?? couponTypesContext?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: vouchers.length });

  const handleEdit = useCallback(
    (voucher: CouponType) => {
      closeAllCards();
      onEditVoucher?.(voucher);
    },
    [onEditVoucher, closeAllCards]
  );

  const handleDelete = useCallback(
    (voucher: CouponType) => {
      closeAllCards();
      onDeleteVoucher?.(voucher);
    },
    [onDeleteVoucher, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = couponTypesContext?.isLoaded ?? dataProp !== undefined;
  const showSkeleton = !isLoaded;

  if (!showSkeleton && vouchers.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vouchers:emptyState.noMatch", { defaultValue: "No vouchers match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("vouchers:emptyState.title", { defaultValue: "No vouchers yet" })}
          description={t("vouchers:emptyState.description", {
            defaultValue: "Create a reusable voucher (e.g. \"Free Drink\") to issue at your events.",
          })}
          iconName="RosetteDiscount"
          actionLabel={t("vouchers:emptyState.actionLabel", { defaultValue: "Add voucher" })}
          onAction={onAddVoucher}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (voucher: CouponType): boolean =>
    isSelectable &&
    ((!!onEditVoucher && recordHasRight(voucher, "edit")) ||
      (!!onDeleteVoucher && recordHasRight(voucher, "remove")));

  const rowProps = (voucher: CouponType): RowProps => ({
    voucher,
    name: voucher.name || "—",
    disabled: isDisabled(voucher),
    onEdit: onEditVoucher ? handleEdit : undefined,
    onDelete: onDeleteVoucher ? handleDelete : undefined,
    highlighted: voucher.id === highlightedId,
    isDeleting: isRemoving(voucher.id),
    language,
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(voucher),
    selected: selection?.isSelected(voucher.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = vouchers
    .filter((v) => rowIsSelectable(v))
    .map((v) => v.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("vouchers:list.columns.name", { defaultValue: "Voucher" })}</Table.Th>
                <Table.Th style={{ width: 320 }}>
                  {t("vouchers:list.columns.items", { defaultValue: "What it gives" })}
                </Table.Th>
                <Table.Th style={{ width: 120 }}>
                  {t("vouchers:list.columns.uses", { defaultValue: "Uses" })}
                </Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t("vouchers:list.columns.createdAt", { defaultValue: "Added" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 8 }).map((_, i) => (
                    <VoucherTableRowSkeleton key={i} selectionColumn={isSelectable} />
                  ))
                : vouchers.map((voucher) => <VoucherTableRow key={voucher.id} {...rowProps(voucher)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <VoucherCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {vouchers.map((voucher) => (
            <motion.div
              key={voucher.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <VoucherCard
                {...rowProps(voucher)}
                forceClose={shouldForceClose(voucher.id || "")}
                onForceCloseComplete={() => handleForceCloseComplete(voucher.id || "")}
                onSwipeStateChange={(isOpen) => handleSwipeStateChange(voucher.id || "", isOpen)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const VouchersList = React.memo(VouchersListComponent);
